<template>
  <TitleLayout>
    <div class="space-y-6 fade-in">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">
            Payoffs
          </h1>
          <p class="text-gray-600 mt-1">Review and update payoff amounts for existing liens.</p>
        </div>
        <button @click="openAddDialog" class="btn btn-primary">
          <font-awesome-icon icon="plus" class="mr-2" />
          Add Payoff
        </button>
      </div>

      <!-- Payoffs Summary -->
      <div class="card">
        <div class="card-header flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900">Total Payoffs</h3>
          <span class="text-2xl font-bold text-primary-600">{{ formatCurrency(totalPayoffs) }}</span>
        </div>
      </div>

      <!-- Payoffs List -->
      <div v-if="payoffs.length > 0" class="space-y-4">
        <div
          v-for="payoff in payoffs"
          :key="payoff.id"
          class="card"
        >
          <div class="card-body">
            <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <!-- Payoff Info -->
              <div class="flex-1">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center">
                    <div class="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <font-awesome-icon icon="landmark" class="text-primary-600" />
                    </div>
                    <div>
                      <h4 class="font-semibold text-gray-900">{{ payoff.creditor_name || 'Unknown Creditor' }}</h4>
                      <span
                        class="status-badge text-xs"
                        :class="getPayoffTypeClass(payoff.type)"
                      >
                        {{ payoff.type || 'Payoff' }}
                      </span>
                    </div>
                  </div>
                  <div class="flex items-center space-x-2">
                    <button
                      @click="openEditDialog(payoff)"
                      class="text-primary-600 hover:text-primary-700"
                      title="Edit"
                    >
                      <font-awesome-icon icon="edit" />
                    </button>
                    <button
                      @click="openDeleteConfirm(payoff)"
                      class="text-danger-600 hover:text-danger-700"
                      title="Delete"
                    >
                      <font-awesome-icon icon="trash" />
                    </button>
                  </div>
                </div>
                
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                  <div>
                    <span class="text-gray-500">Account #</span>
                    <p class="font-medium">{{ payoff.account_number || '-' }}</p>
                  </div>
                  <div>
                    <span class="text-gray-500">Per Diem</span>
                    <p class="font-medium">{{ formatCurrency(payoff.per_diem) }}/day</p>
                  </div>
                  <div>
                    <span class="text-gray-500">Expiration</span>
                    <p class="font-medium">{{ formatDate(payoff.expiration_date) }}</p>
                  </div>
                  <div>
                    <span class="text-gray-500">Balance Due</span>
                    <p class="font-bold text-lg text-primary-600">{{ formatCurrency(payoff.balance_due) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="card">
        <div class="card-body text-center py-12">
          <font-awesome-icon icon="dollar-sign" class="text-4xl mb-4 text-gray-300" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">No Payoffs Found</h3>
          <p class="text-gray-500 mb-4">
            There are no existing liens or payoffs recorded for this property.
          </p>
          <button @click="openAddDialog" class="btn btn-primary">
            <font-awesome-icon icon="plus" class="mr-2" />
            Add Payoff
          </button>
        </div>
      </div>

      <!-- Navigation -->
      <div class="flex items-center justify-between">
        <router-link to="/fees" class="btn btn-secondary">
          <font-awesome-icon icon="arrow-left" class="mr-2" />
          Fees
        </router-link>
        <button @click="continueToNext" class="btn btn-primary">
          Continue to Preview
          <font-awesome-icon icon="arrow-right" class="ml-2" />
        </button>
      </div>
    </div>

    <!-- Add/Edit Payoff Dialog -->
    <Dialog 
      :title="isEditMode ? 'Edit Payoff' : 'Add New Payoff'" 
      :isOpen="isDialogOpen" 
      @close="closeDialog"
      size="md"
    >
      <form @submit.prevent="handleSubmit" class="space-y-4">
        <div>
          <label class="form-label">Creditor Name *</label>
          <input
            v-model="formData.creditorName"
            type="text"
            class="form-input"
            placeholder="e.g., First National Bank"
            required
          />
        </div>

        <div>
          <label class="form-label">Payoff Type *</label>
          <select v-model="formData.type" class="form-select" required>
            <option value="">Select type...</option>
            <option value="Mandatory Obligation">Mandatory Obligation</option>
            <option value="Tax Payoff">Tax Payoff</option>
            <option value="Non-mandatory Payoff">Non-mandatory Payoff</option>
            <option value="Unseasoned HELOC">Unseasoned HELOC</option>
          </select>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="form-label">Account Number</label>
            <input
              v-model="formData.accountNumber"
              type="text"
              class="form-input"
              placeholder="****1234"
            />
          </div>
          <div>
            <label class="form-label">Expiration Date</label>
            <input
              v-model="formData.expirationDate"
              type="date"
              class="form-input"
            />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="form-label">Balance Due *</label>
            <div class="relative">
              <span class="absolute left-3 top-2 text-gray-500">$</span>
              <input
                v-model.number="formData.balanceDue"
                type="number"
                step="0.01"
                min="0"
                class="form-input pl-7"
                placeholder="0.00"
                required
              />
            </div>
          </div>
          <div>
            <label class="form-label">Per Diem</label>
            <div class="relative">
              <span class="absolute left-3 top-2 text-gray-500">$</span>
              <input
                v-model.number="formData.perDiem"
                type="number"
                step="0.01"
                min="0"
                class="form-input pl-7"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        <div>
          <label class="form-label">Payoff Address</label>
          <input
            v-model="formData.address"
            type="text"
            class="form-input"
            placeholder="123 Main St, City, State ZIP"
          />
        </div>

        <!-- Error Message -->
        <div v-if="formError" class="tolerance-exceeded rounded-lg p-3">
          <div class="flex items-center">
            <font-awesome-icon icon="exclamation-circle" class="text-danger-600 mr-2" />
            <span class="text-sm text-danger-800">{{ formError }}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            @click="closeDialog"
            class="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="isSubmitting"
          >
            <font-awesome-icon v-if="isSubmitting" icon="spinner" class="mr-2 animate-spin" />
            {{ isEditMode ? 'Save Changes' : 'Add Payoff' }}
          </button>
        </div>
      </form>
    </Dialog>

    <!-- Delete Confirmation Dialog -->
    <Confirmation
      :isOpen="isDeleteConfirmOpen"
      title="Delete Payoff"
      :message="`Are you sure you want to delete the payoff to '${deletingPayoff?.creditor_name || ''}'?`"
      confirmText="Delete"
      @confirm="handleConfirmDelete"
      @close="closeDeleteConfirm"
    />
  </TitleLayout>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import TitleLayout from '@/components/Layout/TitleLayout.vue'
import Dialog from '@/components/Dialog/index.vue'
import Confirmation from '@/components/Confirmation/index.vue'
import apiService from '@/api/apiService'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Payoffs',
  components: {
    TitleLayout,
    Dialog,
    Confirmation,
  },
  setup() {
    const store = useStore()
    const router = useRouter()

    const payoffs = computed(() => store.getters.payoffs)

    // Dialog state
    const isDialogOpen = ref(false)
    const isEditMode = ref(false)
    const editingPayoffId = ref(null)
    const isSubmitting = ref(false)
    const formError = ref(null)

    // Delete confirmation state
    const isDeleteConfirmOpen = ref(false)
    const deletingPayoff = ref(null)

    const formData = reactive({
      creditorName: '',
      type: '',
      accountNumber: '',
      balanceDue: null,
      perDiem: null,
      expirationDate: '',
      address: '',
    })

    const totalPayoffs = computed(() => {
      return payoffs.value.reduce((sum, p) => {
        const balance = parseFloat(p.balance_due || p.balanceDue || 0)
        return sum + balance
      }, 0)
    })

    const resetForm = () => {
      formData.creditorName = ''
      formData.type = ''
      formData.accountNumber = ''
      formData.balanceDue = null
      formData.perDiem = null
      formData.expirationDate = ''
      formData.address = ''
      formError.value = null
      editingPayoffId.value = null
    }

    const openAddDialog = () => {
      resetForm()
      isEditMode.value = false
      isDialogOpen.value = true
    }

    const openEditDialog = (payoff) => {
      formData.creditorName = payoff.creditor_name || payoff.creditorName || ''
      formData.type = payoff.type || ''
      formData.accountNumber = payoff.account_number || payoff.accountNumber || ''
      formData.balanceDue = payoff.balance_due || payoff.balanceDue
      formData.perDiem = payoff.per_diem || payoff.perDiem
      formData.expirationDate = payoff.expiration_date || payoff.expirationDate || ''
      formData.address = payoff.address || ''
      editingPayoffId.value = payoff.id
      formError.value = null
      isEditMode.value = true
      isDialogOpen.value = true
    }

    const closeDialog = () => {
      isDialogOpen.value = false
      resetForm()
    }

    const fetchPayoffs = async () => {
      try {
        store.dispatch('setLoading', { isLoading: true, message: 'Loading payoffs...' })
        
        const data = await apiService.getPayoffs()
        console.log('[Payoffs] Fetched:', data)

        // Normalize payoff data: convert camelCase to snake_case for consistency
        // Backend returns camelCase (creditorName, balanceDue) but frontend expects snake_case
        const normalizedPayoffs = (data.payoffs || []).map(payoff => {
          const normalized = { ...payoff }
          // Normalize field names from camelCase to snake_case
          if (payoff.creditorName !== undefined) {
            normalized.creditor_name = payoff.creditorName
            delete normalized.creditorName
          }
          if (payoff.accountNumber !== undefined) {
            normalized.account_number = payoff.accountNumber
            delete normalized.accountNumber
          }
          if (payoff.balanceDue !== undefined) {
            normalized.balance_due = payoff.balanceDue
            delete normalized.balanceDue
          }
          if (payoff.perDiem !== undefined) {
            normalized.per_diem = payoff.perDiem
            delete normalized.perDiem
          }
          if (payoff.expirationDate !== undefined) {
            normalized.expiration_date = payoff.expirationDate
            delete normalized.expirationDate
          }
          return normalized
        })

        store.dispatch('setPayoffs', normalizedPayoffs)
      } catch (err) {
        console.error('[Payoffs] Failed to fetch:', err)
      } finally {
        store.dispatch('setLoading', false)
      }
    }

    const handleSubmit = async () => {
      isSubmitting.value = true
      formError.value = null

      try {
        const payload = {
          creditor_name: formData.creditorName,
          type: formData.type,
          account_number: formData.accountNumber,
          balance_due: formData.balanceDue,
          per_diem: formData.perDiem,
          expiration_date: formData.expirationDate || null,
          address: formData.address,
        }

        if (isEditMode.value) {
          await apiService.updatePayoff(editingPayoffId.value, payload)
          console.log('[Payoffs] Updated payoff')
        } else {
          await apiService.createPayoff(payload)
        }

        // Refresh payoffs list
        await fetchPayoffs()
        
        // Close dialog
        closeDialog()
        
        // Sync closing status from API (backend sets payoffs_complete automatically)
        await syncClosingStatus()

      } catch (err) {
        console.error('[Payoffs] Failed to save:', err)
        formError.value = err.response?.data?.detail || 'Failed to save payoff. Please try again.'
      } finally {
        isSubmitting.value = false
      }
    }

    const openDeleteConfirm = (payoff) => {
      deletingPayoff.value = payoff
      isDeleteConfirmOpen.value = true
    }

    const closeDeleteConfirm = () => {
      isDeleteConfirmOpen.value = false
      deletingPayoff.value = null
    }

    const handleConfirmDelete = async () => {
      if (!deletingPayoff.value) return

      try {
        // Note: Delete endpoint may not be available, refresh from API
        await fetchPayoffs()
        closeDeleteConfirm()
      } catch (err) {
        console.error('[Payoffs] Failed to delete:', err)
      }
    }

    const formatCurrency = (value) => {
      if (value === null || value === undefined) return '$0.00'
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value)
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    }

    const getPayoffTypeClass = (type) => {
      switch (type) {
        case 'Mandatory Obligation':
          return 'status-danger'
        case 'Tax Payoff':
          return 'status-warning'
        case 'Unseasoned HELOC':
          return 'status-warning'
        default:
          return 'status-pending'
      }
    }

    const syncClosingStatus = async () => {
      try {
        const data = await apiService.getClosingStatus()
        
        // Update local store with API status (backend is source of truth)
        if (data.progress) {
          store.dispatch('setClosingProgress', {
            titleInfoComplete: data.progress.title_info_complete || false,
            feesComplete: data.progress.fees_complete || false,
            payoffsComplete: data.progress.payoffs_complete || false,
            previewGenerated: data.progress.preview_generated || false,
            documentsGenerated: data.progress.documents_generated || false,
          })
        }
      } catch (err) {
        console.error('[Payoffs] Failed to sync closing status:', err)
      }
    }

    const continueToNext = async () => {
      try {
        // Update closing status via PATCH to mark payoffs as complete
        await apiService.updateClosingStatus({
          payoffs_complete: true,
        })
        console.log('[Payoffs] Updated closing status: payoffs_complete = true')
        
        // Sync with backend to get updated status
        await syncClosingStatus()
      } catch (err) {
        console.error('[Payoffs] Failed to update closing status:', err)
        // Still continue even if update fails
      }
      
      router.push('/preview')
    }

    onMounted(() => {
      fetchPayoffs()
    })

    return {
      payoffs,
      totalPayoffs,
      isDialogOpen,
      isEditMode,
      isSubmitting,
      formData,
      formError,
      isDeleteConfirmOpen,
      deletingPayoff,
      openAddDialog,
      openEditDialog,
      closeDialog,
      openDeleteConfirm,
      continueToNext,
      closeDeleteConfirm,
      handleSubmit,
      handleConfirmDelete,
      formatCurrency,
      formatDate,
      getPayoffTypeClass,
      syncClosingStatus,
    }
  },
}
</script>
